import { useCallback, useMemo } from 'react';

import { useDockets } from '@/hooks/useDockets';
import type { Docket, DocketCandidate } from './schema';

export interface LinkedDocketItem {
  docket: Docket;
}

/**
 * Adapter over {@link useDockets} that exposes the matter's linked dockets and
 * link/unlink actions backed by the real provider-agnostic dockets API.
 */
export function useLinkedDocket(matterGuid: string | undefined) {
  const { dockets, error, isLoading, linkDocket, unlinkDocket } = useDockets(matterGuid);

  const linkedDockets: LinkedDocketItem[] = useMemo(
    () => dockets.map((docket) => ({ docket })),
    [dockets],
  );

  const linkedIds = useMemo(() => dockets.map((docket) => docket.id), [dockets]);

  const link = useCallback(
    (candidate: DocketCandidate): Promise<Docket> =>
      linkDocket({
        sourceProvider: candidate.sourceProvider,
        sourceDocketId: candidate.sourceDocketId,
      }),
    [linkDocket],
  );

  return {
    linkedDockets,
    linkedIds,
    isLoading,
    error,
    linkDocket: link,
    unlinkDocket,
  };
}

export default useLinkedDocket;
